/**
 * Model B: one disposable sub-wallet per interaction plus automatic claw-back.
 *
 * The contract never had "the" sub-wallet. Ownership lives in control notes,
 * one per sub, and any number of subs can exist. This module holds the pure
 * rules; the session drives chain I/O and proofs.
 *
 * - A sub whose control note still has nonce 0 has never executed a call or a
 *   shield. It is "fresh" and may carry a new interaction.
 * - Every other sub is "used". It stays the owner of whatever position it
 *   opened (stake, loan, order) and is only driven again when the user
 *   manages that position.
 * - Settled bank balance in any sub is clawed back to the Private Reserve,
 *   except dust and balances of a sub with an operation in flight.
 */
import type { V2Call, V2Coin } from "./action";
import { base64Decode } from "./encoding";
import type { ControlNote } from "./notes";
import { denomSymbol } from "./operationSteps";
import { protocolFeeCoins } from "./safety";

export type SubwalletRole = "fresh" | "used";

export type SubwalletInfo = {
  address: string;
  role: SubwalletRole;
  /** Leaf index of the current control note; larger means more recent. */
  index: number;
  controlNonce: bigint;
};

type ControlLike = { index: number; note: ControlNote };

const MODEL_B_STORAGE_KEY = "rd:model-b";

/** Model B is on by default in this build and can be switched off per browser. */
export const isModelBEnabled = (): boolean => {
  const env = import.meta.env?.VITE_REDACTED_V2_MODEL_B;
  if (env === "false") return false;
  try {
    return globalThis.localStorage?.getItem(MODEL_B_STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
};

export const classifySubwallets = (
  controls: readonly ControlLike[]
): SubwalletInfo[] =>
  controls
    .filter((control) => !!control.note.subwallet)
    .map((control) => {
      // A note without a nonce has never been used; never crash recovery.
      const controlNonce = /^\d+$/.test(String(control.note.controlNonce))
        ? BigInt(control.note.controlNonce)
        : 0n;
      return {
        address: control.note.subwallet as string,
        role: controlNonce === 0n ? ("fresh" as const) : ("used" as const),
        index: control.index,
        controlNonce,
      };
    })
    .sort((left, right) => left.index - right.index);

/**
 * How private actions pick their sub-wallet.
 * - "single" (Model A, default): every action uses the account's one Spending
 *   sub; the Reserve funds it on demand and settled balances are clawed back.
 *   The user can switch to a new Spending sub at any time (spending.ts).
 * - "rotate" (Model B): a fresh one-time sub per interaction, a position keeps
 *   its own sub.
 */
export type PrivateWalletMode = "single" | "rotate";

const WALLET_MODE_STORAGE_KEY = "rd:wallet-mode";

export const privateWalletMode = (): PrivateWalletMode => {
  const env = import.meta.env?.VITE_REDACTED_V2_WALLET_MODE;
  try {
    const stored = globalThis.localStorage?.getItem(WALLET_MODE_STORAGE_KEY);
    if (stored === "rotate" || stored === "single") return stored;
  } catch {
    /* No storage: use the build default. */
  }
  return env === "rotate" ? "rotate" : "single";
};

/**
 * The account's Spending sub in single mode before any new Spending account
 * was created: the most used one (highest control nonce). After rotation
 * tests the original, most used sub stays the Spending sub. Deterministic on
 * every device, since it derives from recovered control notes only.
 */
export const primarySubwallet = (
  subwallets: readonly SubwalletInfo[]
): string | undefined =>
  [...subwallets].sort((left, right) =>
    left.controlNonce === right.controlNonce
      ? left.index - right.index
      : left.controlNonce > right.controlNonce
        ? -1
        : 1
  )[0]?.address;

/**
 * The Spending sub in single mode: the newest new Spending account (derived
 * salt, see spending.ts) when the account has one, else the most used sub.
 * Both derive from chain data and the keys only: the same on every device.
 */
export const spendingSubwallet = (
  subwallets: readonly SubwalletInfo[],
  regenerated?: string
): string | undefined =>
  regenerated && subwallets.some((sub) => sub.address === regenerated)
    ? regenerated
    : primarySubwallet(subwallets);

export type InteractionOptions = {
  /** Single mode: the newest new Spending account, if there is one. */
  spending?: string;
  /**
   * Single mode: older subs that hold positions. A pin to one of them acts
   * from it (closing or managing its own position).
   */
  positionAccounts?: ReadonlySet<string>;
};

/**
 * The sub that carries the next interaction. Single mode: the Spending sub,
 * except that a pinned position account manages its own positions. Rotate
 * mode: a pinned position sub wins; else the oldest fresh sub (creation time
 * decorrelated from use time). Undefined means a new sub has to be created
 * first.
 */
export const nextInteractionSubwallet = (
  subwallets: readonly SubwalletInfo[],
  pinned?: string,
  mode: PrivateWalletMode = privateWalletMode(),
  options: InteractionOptions = {}
): string | undefined => {
  if (mode === "single") {
    const spending = spendingSubwallet(subwallets, options.spending);
    if (
      pinned &&
      pinned !== spending &&
      options.positionAccounts?.has(pinned) &&
      subwallets.some((sub) => sub.address === pinned)
    )
      return pinned;
    return spending;
  }
  if (pinned && subwallets.some((sub) => sub.address === pinned)) return pinned;
  return subwallets.find((sub) => sub.role === "fresh")?.address;
};

/**
 * Smallest reserve transfer X whose credited amount covers `needed`.
 *
 * Reserve to sub-wallet transfers deduct the protocol fee from the spent
 * amount (fee = X * bps / 10000, rounded per contract mode). The sub receives
 * X - fee(X), which must be at least `needed`.
 */
export const grossUpForDeductedFee = (
  needed: bigint,
  feeBps: number,
  rounding: "floor" | "ceil",
  deducted = true
): bigint => {
  if (needed <= 0n) return 0n;
  if (!deducted || feeBps === 0) return needed;
  const fee = (amount: bigint): bigint =>
    BigInt(
      protocolFeeCoins(new Map([["x", amount]]), feeBps, rounding)[0]?.amount ??
        "0"
    );
  let amount =
    needed +
    (needed * BigInt(feeBps) + (10_000n - BigInt(feeBps) - 1n)) /
      (10_000n - BigInt(feeBps));
  // Converge from the analytic estimate to the exact minimum.
  while (amount - fee(amount) < needed) amount += 1n;
  while (amount > needed && amount - 1n - fee(amount - 1n) >= needed)
    amount -= 1n;
  return amount;
};

export type FundingLeg = {
  denom: string;
  /** What the sub must hold after funding. */
  needed: string;
  /** Already in the sub. */
  present: string;
  /** Spent from the Private Reserve (includes the deducted protocol fee). */
  transfer: string;
  /** Protocol fee deducted from this transfer. */
  fee: string;
  /** Relayer fee paid from the note when transfers are not sponsored. */
  relayerFee?: string;
};

/** Funding legs for a call whose sub must hold `debit` before execution. */
export const planFunding = (params: {
  debit: readonly Readonly<V2Coin>[];
  present: ReadonlyMap<string, bigint>;
  reserve: ReadonlyMap<string, bigint>;
  feeBps: number;
  rounding: "floor" | "ceil";
  /** Whether transfers into a sub deduct a protocol fee in this deployment. */
  deducted: boolean;
  /** Relayer fee per transfer when transfers are not sponsored. */
  transferRelayerFee?: (denom: string) => bigint;
}): FundingLeg[] =>
  params.debit
    .map((coin) => {
      const needed = BigInt(coin.amount);
      const present = params.present.get(coin.denom) ?? 0n;
      const transfer = grossUpForDeductedFee(
        needed - present,
        params.feeBps,
        params.rounding,
        params.deducted
      );
      const relayerFee =
        transfer > 0n ? (params.transferRelayerFee?.(coin.denom) ?? 0n) : 0n;
      const available = params.reserve.get(coin.denom) ?? 0n;
      if (transfer + relayerFee > available)
        throw new Error(`Insufficient ${denomSymbol(coin.denom)} balance`);
      const fee = transfer === 0n ? 0n : transfer - (needed - present);
      return {
        denom: coin.denom,
        needed: needed.toString(),
        present: present.toString(),
        transfer: transfer.toString(),
        fee: (fee < 0n ? 0n : fee).toString(),
        ...(relayerFee > 0n && { relayerFee: relayerFee.toString() }),
      };
    })
    .filter((leg) => BigInt(leg.transfer) > 0n);

export type SweepCandidate = { subwallet: string; coin: V2Coin };

/**
 * Receipt and share tokens represent an open position (liquid stake, LP,
 * lending vault, index). Product pages read them from the sub that holds
 * them, so they are never clawed back as idle cash; their underlying assets
 * return once the user redeems.
 */
const POSITION_TOKEN_PREFIXES = [
  "x/staking-",
  "x/stake-receipt",
  "x/ghost-vault",
  "x/nami-index-",
  "x/bow-",
];

export const isPositionToken = (denom: string): boolean =>
  POSITION_TOKEN_PREFIXES.some((prefix) => denom.startsWith(prefix));

/**
 * Balance-driven claw-back: every settled coin in any owned sub returns to
 * the Reserve. No position tracking needed: value locked in a position is not
 * bank balance, so the sub looks empty until the position pays out.
 */
export const planSweep = (params: {
  balances: ReadonlyMap<string, readonly Readonly<V2Coin>[]>;
  /** Subs with an operation in flight (just funded, not yet executed). */
  busy: ReadonlySet<string>;
  isDust: (coin: Readonly<V2Coin>) => boolean;
}): SweepCandidate[] =>
  [...params.balances]
    .filter(([subwallet]) => !params.busy.has(subwallet))
    .flatMap(([subwallet, coins]) =>
      coins
        .filter(
          (coin) =>
            BigInt(coin.amount) > 0n &&
            !isPositionToken(coin.denom) &&
            !params.isDust(coin)
        )
        .map((coin) => ({ subwallet, coin: { ...coin } }))
    );

/** Randomized delay so a claw-back does not land next to its action. */
export const sweepDelayMs = (
  random: () => number = Math.random,
  override?: number
): number => {
  if (override !== undefined && Number.isFinite(override) && override >= 0)
    return override;
  const min = 45_000;
  const max = 180_000;
  return Math.round(min + random() * (max - min));
};

const SWEEP_DELAY_STORAGE_KEY = "rd:sweep-delay-ms";

/** Development override for the claw-back delay (milliseconds). */
export const sweepDelayOverride = (): number | undefined => {
  try {
    const raw = globalThis.localStorage?.getItem(SWEEP_DELAY_STORAGE_KEY);
    if (raw === null || raw === undefined || raw === "") return undefined;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : undefined;
  } catch {
    return undefined;
  }
};

const SWAP_MEMO = /^(=|s|swap)[:<>]/i;

/**
 * One-shot interactions: market swaps (FIN, Rujira router, THORChain). Their
 * sub is never reused. Everything else opens or changes a position, whose sub
 * stays pinned so the product page keeps showing that position.
 */
export const isOneShotIntent = (calls: readonly V2Call[]): boolean =>
  calls.length > 0 &&
  calls.every((call) => {
    if ("thorchain_deposit" in call)
      return SWAP_MEMO.test(call.thorchain_deposit.memo);
    if ("wasm_execute" in call) {
      try {
        const parsed = JSON.parse(
          new TextDecoder().decode(base64Decode(call.wasm_execute.msg))
        );
        const key =
          parsed && typeof parsed === "object" ? Object.keys(parsed)[0] : "";
        return key === "swap" || key === "market_swap";
      } catch {
        return false;
      }
    }
    return false;
  });

type ReserveLike = {
  nullifier: string;
  note: { kind: string; denom?: string; amount?: string };
};

/**
 * Reserve housekeeping: the two smallest notes of the most fragmented denom.
 * Every claw-back adds a note; merging while idle keeps funding to one proof.
 */
export const pickReserveMerge = (
  notes: readonly ReserveLike[]
): readonly [string, string] | undefined => {
  const byDenom = new Map<string, ReserveLike[]>();
  for (const entry of notes) {
    if (entry.note.kind !== "reserve" || !entry.note.denom) continue;
    const list = byDenom.get(entry.note.denom) ?? [];
    list.push(entry);
    byDenom.set(entry.note.denom, list);
  }
  const fragmented = [...byDenom.values()]
    .filter((list) => list.length >= 2)
    .sort((left, right) => right.length - left.length)[0];
  if (!fragmented) return undefined;
  const [first, second] = [...fragmented].sort((left, right) => {
    const a = BigInt(left.note.amount ?? "0");
    const b = BigInt(right.note.amount ?? "0");
    return a < b ? -1 : a > b ? 1 : 0;
  });
  return [first.nullifier, second.nullifier] as const;
};

/**
 * A move between the Private Reserve and a sub that is confirmed on chain but
 * not yet in the published Reserve. Balances shown to the user combine the
 * Reserve (from recovery) with the sub (from the indexer); the two update at
 * different times, so without this a funded amount would briefly count twice
 * (or a returned amount not at all).
 */
export type PendingMove = {
  denom: string;
  direction: "fund" | "return";
  /** Sub balance before the move. */
  subBefore: bigint;
  /** What the sub gains (fund) or loses (return). */
  subDelta: bigint;
  /** What the Reserve loses (fund) or gains (return). */
  reserveDelta: bigint;
  /** The action that used the funds is confirmed: the Reserve side is final. */
  settled?: boolean;
};

/**
 * Reserve amount to show next to a sub balance of `shownSub`. A funding move
 * leaves the Reserve once the sub shows it (or its action settled); a return
 * joins the Reserve once the sub no longer shows it.
 */
export const displayedReserve = (
  denom: string,
  reserve: bigint,
  shownSub: bigint,
  moves: readonly PendingMove[]
): bigint => {
  const amount = moves
    .filter((move) => move.denom === denom)
    .reduce((value, move) => {
      if (move.direction === "fund")
        return move.settled || shownSub >= move.subBefore + move.subDelta
          ? value - move.reserveDelta
          : value;
      return shownSub <= move.subBefore - move.subDelta
        ? value + move.reserveDelta
        : value;
    }, reserve);
  return amount > 0n ? amount : 0n;
};
